import { createEvent, createStore, sample } from "effector";
import { SolidFormResponse, SolidgatePlan } from "@features/shared/entities/payment";
import type { Store, Effect } from "effector";

export const $walletActiveForm = createStore<SolidFormResponse | null>(null);
export const $walletIsUpdating = createStore<boolean>(false);

const $walletCache = createStore<Record<string, SolidFormResponse>>({});

const $walletLastRequestId = createStore<number>(0);

const $walletCurrentKey = createStore<string | null>(null);

export const $lastWalletRequest = createStore<{ reqId: number; key: string } | null>(null);
const walletSelectionChanged = createEvent<{ planId: string; priceIndex: number; currency: string; key: string }>();
const walletCacheHit = createEvent<SolidFormResponse>();
const walletPrefetchRequested = createEvent<{ planId: string; currency: string; key: string; reqId: number }>();
const updateWalletCache = createEvent<{ key: string; response: SolidFormResponse }>();

sample({
    clock: walletSelectionChanged,
    fn: ({ key }) => key,
    target: $walletCurrentKey
});

sample({
    clock: walletCacheHit,
    target: $walletActiveForm
});

sample({
    clock: walletPrefetchRequested,
    fn: () => true,
    target: $walletIsUpdating
});

sample({
    clock: walletPrefetchRequested,
    fn: ({ reqId }) => reqId,
    target: $walletLastRequestId
});

sample({
    clock: updateWalletCache,
    source: $walletCache,
    fn: (cache, { key, response }) => ({ ...cache, [key]: response }),
    target: $walletCache
});

sample({
    clock: $walletActiveForm.updates,
    fn: () => false,
    target: $walletIsUpdating
});

export function connectWalletLogic(params: {
    $currentSolidgatePlan: Store<SolidgatePlan | null>;
    $solidgatePlanPriceIndex: Store<number>;
    $currency: Store<string>;
    $isCheckoutPopupOpen: Store<boolean>;
    getSolidgateFormFx: Effect<{ currency: string; planId?: string }, SolidFormResponse | null>;
}) {
    const { $currentSolidgatePlan, $solidgatePlanPriceIndex, $currency, $isCheckoutPopupOpen, getSolidgateFormFx } = params;

    const walletFormRequested = createEvent<{ planId: string; currency: string; reqId: number; key: string }>();
    const walletFormResponse = createEvent<{ response: SolidFormResponse | null; reqId: number; key: string }>();

    sample({
        clock: walletFormRequested,
        fn: ({ reqId, key }) => ({ reqId, key }),
        target: $lastWalletRequest
    });

    sample({
        clock: walletFormRequested,
        fn: ({ planId, currency }) => ({ planId, currency }),
        target: getSolidgateFormFx
    });

    sample({
        clock: getSolidgateFormFx.doneData,
        source: $lastWalletRequest,
        filter: (request) => request !== null,
        fn: (request, response) => ({
            response,
            reqId: request!.reqId,
            key: request!.key
        }),
        target: walletFormResponse
    });

    sample({
        clock: walletFormResponse,
        fn: () => null,
        target: $lastWalletRequest
    });

    const walletKeyComputed = sample({
        clock: [$currentSolidgatePlan, $solidgatePlanPriceIndex, $currency],
        source: {
            plan: $currentSolidgatePlan,
            idx: $solidgatePlanPriceIndex,
            currency: $currency,
            currentKey: $walletCurrentKey
        },
        filter: ({ plan }) => !!plan,
        fn: ({ plan, idx, currency, currentKey }) => {
            const prices = plan!.prices ?? [];
            const price = prices[idx] ?? null;
            const cur = price?.currency ?? currency;
            const key = `${plan!.id}:${idx}:${cur}`;

            if (currentKey === key) {
                return null;
            }

            return { planId: plan!.id, priceIndex: idx, currency: cur, key };
        }
    });

    sample({
        clock: walletKeyComputed,
        filter: (data): data is { planId: string; priceIndex: number; currency: string; key: string } => data !== null,
        target: walletSelectionChanged
    });

    sample({
        clock: walletPrefetchRequested,
        target: walletFormRequested
    });

    sample({
        clock: walletSelectionChanged,
        source: { cache: $walletCache },
        filter: ({ cache }, { key }) => !!cache[key],
        fn: ({ cache }, { key }) => cache[key],
        target: walletCacheHit
    });

    sample({
        clock: walletSelectionChanged,
        source: { cache: $walletCache, lastReqId: $walletLastRequestId },
        filter: ({ cache }, { key }) => !cache[key],
        fn: ({ lastReqId }, { key, planId, currency }) => ({
            planId,
            currency,
            key,
            reqId: lastReqId + 1
        }),
        target: walletPrefetchRequested
    });

    const walletResponseValidated = sample({
        clock: walletFormResponse,
        source: {
            isOpen: $isCheckoutPopupOpen,
            lastReqId: $walletLastRequestId,
            currentKey: $walletCurrentKey
        },
        filter: ({ isOpen }) => !isOpen,
        fn: ({ lastReqId, currentKey }, data) => {
            if (
                data.reqId !== lastReqId ||
                !data.key ||
                !data.response ||
                data.key !== currentKey
            ) {
                return null;
            }
            return { key: data.key, response: data.response };
        }
    });

    sample({
        clock: walletResponseValidated,
        filter: (data) => data !== null && !!data.key && !!data.response,
        fn: (data) => ({ key: data!.key, response: data!.response }),
        target: updateWalletCache
    });

    sample({
        clock: walletResponseValidated,
        filter: (data) => data !== null && !!data.response,
        fn: (data) => data!.response,
        target: $walletActiveForm
    });
}
